// this interface is meant to generate static urls from a constant file
// and dymanically in the future (e.g. specific studio venue/class urls)
import { format } from 'date-fns';
import LINKS from 'constants/links';
import { shortCountryCodeToCountryCode } from 'constants/countryCodeToStaticValueMaps';
import qs from 'qs';
import { formatRoute } from './format-route';
import { isVenueDashboardSubtypeFoodAndBeverageById } from './ia-logic';

const firstOrEmpty = list => (list.length > 0 ? list[0] : {});

const getLanguage = locale => (locale ? locale.split('-')[0] : 'en_US');

export const makePath = {
  accountSettings: () => '/account/profile',
  autoSync: venueId => `/venues/${venueId}/edit/auto-sync`,
  approvedWellnessServices: (locale = 'en_US') =>
    `https://partnersupport.classpass.com/help/s/article/approved-wellness-services?language=${getLanguage(locale)}`,
  campaigns: ({ venueId }) => `/campaigns/${venueId}`,
  campaignsAdd: ({ venueId }) => `/campaigns/${venueId}/add`,
  campaignsEdit: ({ venueId, partnerCampaignId }) => `/campaigns/${venueId}/${partnerCampaignId}/edit`,
  classes: ({ venueId, startDate, scheduleId, tab, focus } = {}) =>
    [
      '/classes',
      ...(venueId ? [venueId] : []),
      ...(venueId && startDate ? [startDate instanceof Date ? format(startDate, 'yyyy-MM-dd') : startDate] : []),
      ...(scheduleId ? [scheduleId] : []),
      ...(scheduleId && tab ? [tab] : []),
    ].join('/') + (focus ? `?focus=${focus}` : ''),
  classInsights: ({ venueId }) => `/stats/${venueId}/class-insights`,
  contactUs: (venueId, locale = 'en_US') => {
    if (!locale) {
      locale = 'en_US';
    }
    const SALESFORCE_LANGUAGES = [
      'da',
      'de',
      'en_US',
      'es',
      'es_MX',
      'fi',
      'fr',
      'it',
      'ja',
      'ko',
      'nl_NL',
      'no',
      'pt_BR',
      'ru',
      'sv',
      'th',
      'zh_CN',
      'zh_TW',
      'ar',
      'bg',
      'cs',
      'el',
      'en_GB',
      'hr',
      'hu',
      'in',
      'iw',
      'pl',
      'pt_PT',
      'ro',
      'sk',
      'sl',
      'tr',
      'uk',
      'vi',
    ];
    const underscoreLocale = locale.replace('-', '_');
    const languageOnlyLocale = locale.split('-')[0];
    const fullMatch = SALESFORCE_LANGUAGES.find(x => x === underscoreLocale);
    const partialMatch = SALESFORCE_LANGUAGES.find(x => x === languageOnlyLocale);
    const language = fullMatch || partialMatch || 'en_US';
    const contactFormUrl = 'https://partnersupport.classpass.com/help/s/contact-account-management';
    return `${contactFormUrl}?language=${language}&venueId=${venueId}`;
  },
  errorPage: () => '/error',
  instructors: ({ venueId, focus = 'list' }) => `/manage/${venueId}/instructors?focus=${focus}`,
  instructorsCreate: ({ venueId }) => `/manage/${venueId}/instructors/create`,
  instructorsEdit: ({ venueId, instructorId }) => `/manage/${venueId}/instructors/${instructorId}/edit`,
  integratorsHelp: (locale = 'en_US') =>
    `https://partnersupport.classpass.com/help/s/article/what-scheduling-platforms-does-classpass-integrate-with?language=${getLanguage(
      locale
    )}`,
  appointments: venueId => `/appointments/${venueId}`,
  orders: venueId => `/orders/${venueId}`,
  appointmentsCalendar: ({ venueId, startDate, practitionerId, view }) => {
    const query = qs.stringify({ startDate, practitionerId });

    return `/appointments/${venueId}/calendar/${view || 'day'}?${query}`;
  },
  appointmentsCalSync: venueId => `/appointments/${venueId}/calendar-links`,
  appointmentsCancel: (venueId, reservationId) => `/appointments/${venueId}/cancel/${reservationId}`,
  appointmentsAlternativeTime: (venueId, reservationId, from) =>
    `/appointments/${venueId}/alternative-time/${reservationId}?&from=${from}`,
  addBlockOffTimeStaff: venueId => `/appointments/${venueId}/block-off-time/staff/add`,
  editBlockOffTimeStaff: (venueId, blockId) => `/appointments/${venueId}/block-off-time/staff/edit/${blockId}`,
  calendarLinks: venueId => `/appointments/${venueId}/calendar-links`,
  opportunities: venueId => `/classes/${venueId}/opportunities`,
  editVenue: (venueId, tab, hash) => {
    let parts = '/venues';
    if (venueId) parts += `/${venueId}/edit`;
    if (tab) parts += `/${tab}`;
    if (hash) parts += `#${hash}`;
    return parts;
  },
  editVenueDynamicPricing: venueId => `/venues/${venueId}/dynamic-pricing`,
  editVenueProfile: venueId => `/venues/${venueId}/edit/settings`,
  editVenuePhotos: venueId => `/venues/${venueId}/edit/profile`,
  editClass: (venueId, classId) => {
    const parts = ['/venues', venueId, 'classes'];
    if (classId) {
      parts.push(classId);
    }
    return parts.join('/');
  },
  feedbackSurvey: userId =>
    `https://survey.alchemer.com/s3/8762557/ClassPass-Partner-Feedback?user_id=${encodeURIComponent(
      String(userId ?? '')
    )}`,
  firstClassFreeFAQs: (locale = 'en_US') =>
    `https://partnersupport.classpass.com/help/s/article/first-class-free?language=${getLanguage(locale)}`,
  lateCancelFAQs: (locale = 'en_US') =>
    `https://partnersupport.classpass.com/help/s/article/will-i-be-paid-for-a-spot-if-a-user-late-cancels-or-misses-my-class?language=${getLanguage(
      locale
    )}`,
  reservationStatusesFAQs: (locale = 'en_US') =>
    `https://classpass.my.site.com/help/s/article/what-does-each-status-mean-in-my-reservations-report?language=${getLanguage(
      locale
    )}`,
  reportUserFAQs: (locale = 'en_US') =>
    `https://partnersupport.classpass.com/help/s/article/How-to-report-a-ClassPass-user?language=${getLanguage(
      locale
    )}`,
  login: () => '/login',
  loginMFACode: () => '/login/mfa',
  loginMFARecoveryCode: () => '/login/mfa/recovery',
  home: venueId => {
    const parts = ['/home'];
    if (venueId) {
      parts.push(venueId);
    }
    return parts.join('/');
  },
  integrations: venueId => (venueId ? `/integrations/${venueId}` : '/integrations'),
  items: venueId => (venueId ? `/items/${venueId}` : '/items'),
  manageServices: ({ venueId, tab = 'services', itemId, focus = 'list' }) => {
    const parts = [`/manage/services/${venueId}/${tab}`];
    if (itemId) parts.push(itemId);
    return `${parts.join('/')}?focus=${focus}`;
  },
  manageServicesAdd: ({ venueId, tab, focus = 'list' }) => {
    return `/manage/services/${venueId}/${tab}/add?focus=${focus}`;
  },
  manageServicesEdit: ({ venueId, tab, itemId, focus = 'list' }) => {
    return `/manage/services/${venueId}/${tab}/${itemId}/edit?focus=${focus}`;
  },
  manageServicesPractitioners: ({ venueId }) => `/manage/services/${venueId}/practitioners`,
  partnerTaxDefinition: locale =>
    `https://partnersupport.classpass.com/help/s/article/Partner-Tax-Definitions?language=${getLanguage(locale)}`,
  pike13: (venueId, params) => {
    let query = !params ? '' : qs.stringify(params);
    query = !query ? '' : `?${query}`;
    return `/migrate/${venueId}/pike13${query}`;
  },
  premiumCaps: venueId => `/venues/${venueId}/premium-caps`,
  ratings: ({ venueId, instructorId, classId, initialVisits, tab }) => {
    const query = qs.stringify({
      ...(classId && { c: classId }),
      ...(instructorId && { i: instructorId }),
      ...(initialVisits && { v: initialVisits }),
    });
    return `/ratings/${venueId}/${tab || 'overview'}?${query}`;
  },
  reportsIndividualScheduleView: scheduleId => `/reports/classes/${scheduleId}`,
  schedules: ({ venueId, startDate, focus = 'list' } = {}) => {
    const parts = [
      '/manage/schedule/generate',
      ...(venueId ? [venueId] : []),
      ...(venueId && startDate ? [format(startDate, 'yyyy-MM-dd')] : []),
    ].join('/');

    return `${parts}?focus=${focus}`;
  },
  schedulesCreate: ({ venueId, startDate, focus = 'list' }) => {
    const parts = [
      '/manage/schedule/generate',
      ...(venueId ? [venueId] : []),
      ...(venueId && startDate ? [format(startDate, 'yyyy-MM-dd')] : []),
      ...(venueId && startDate ? ['create'] : ''),
    ].join('/');
    return `${parts}?focus=${focus}`;
  },
  schedulesEdit: ({ venueId, id, startDate, focus = 'list', pageSource = 'schedules' } = {}) => {
    const parts = [
      '/manage/schedule/generate',
      ...(venueId ? [venueId] : []),
      ...(venueId && startDate ? [format(startDate, 'yyyy-MM-dd')] : []),
      ...(venueId && (startDate || id) ? ['edit'] : ''),
      ...(venueId && id ? [id] : ''),
    ].join('/');
    return `${parts}?focus=${focus}&source=${pageSource}`;
  },
  scheduleInstanceEdit: ({ venueId, scheduleId, occurrenceDate, focus = 'list', pageSource = 'schedules' } = {}) => {
    const parts = [
      '/manage/schedule/generate',
      ...(venueId ? [venueId] : []),
      ...(venueId && scheduleId ? ['edit-instance', scheduleId] : ''),
    ].join('/');
    return `${parts}?&focus=${focus}&occurrenceDate=${occurrenceDate}&source=${pageSource}`;
  },
  scheduleIncrements: venueId => `/manage/services/${venueId}/schedule-increments`,
  smartRate: venueId => `/smartrate/${venueId}`,
  smartTools: venueId => `/manage/${venueId}/smarttools`,
  skuPayoutReports: () => '/stats/reports/payouts/orders',
  skuPayoutReportDetail: payoutId => `/stats/reports/payouts/orders/${payoutId}`,
  reports: ({ reportType, id } = {}, params) => {
    const parts = ['/stats/reports'];

    if (reportType) {
      parts.push(reportType);

      if (id) {
        parts.push(id);
      }
    }

    let path = parts.join('/');

    if (params) {
      path += `?${qs.stringify(params)}`;
    }

    return path;
  },
  stats: (tab, { venueId } = {}, params = {}) => {
    if (tab === 'reports') {
      return formatRoute('/stats/reports', params);
    }

    return formatRoute(`/stats/${venueId}/${tab}`, params);
  },
  statsDynamic: venueId => {
    const isFoodAndBeverageVenue = isVenueDashboardSubtypeFoodAndBeverageById(venueId);

    if (isFoodAndBeverageVenue) {
      return makePath.stats('reports', { venueId });
    }

    return makePath.stats('your-insights', { venueId });
  },
  viewSignedWaiver: ({ venueId, startDate, scheduleId, reservationId, waiverId, waiverVersionId }) =>
    `/classes/${venueId}/${startDate}/${scheduleId}/reservation/${reservationId}/waiver/${waiverId}/${waiverVersionId}`,
};

export const testPath = {
  appointments: path => /\/appointments\/\d+\/?$/.test(path),
  orders: path => /\/orders\/\d+\/?$/.test(path),
  appointmentsCalendar: path => /\/appointments\/\d+\/calendar\/day|week/.test(path),
  appointmentsCalSync: path => /\/appointments\/\d+\/calendar-links/.test(path),
  campaigns: path => /\/campaigns\/\d+\/?$/.test(path),
  classes: path => /^\/classes(\/\d+)?(\/\d{4}-\d{2}-\d{2})?(\/\d+)?(\/(insights|roster))?\/?$/.test(path),
  editClassPage: path => /\/venues(\/\d+)\/classes(\/\d+)?/.test(path),
  editBusinessProfile: path => /\/venues(\/\d+)\/edit/.test(path),
  instructors: path => /\/manage(\/\d+)\/instructors/.test(path),
  services: path => /\/manage\/services(\/\d+)\/services/.test(path),
  practitioners: path => /\/manage\/services(\/\d+)\/practitioners/.test(path),
  equipment: path => /\/manage\/services(\/\d+)\/resources/.test(path),
  opportunities: path => /\/classes\/\d+\/opportunities\/?$/.test(path),
  ratings: path => /\/ratings\/\d+\/overview|reviews/.test(path),
  reportSchedule: path => /\/reports\/classes\/\d+$/.test(path),
  schedules: path => /\/manage\/schedule\/generate\/\d+?(\/\d{4}-\d{2}-\d{2})?/.test(path),
  smartTools: path => /\/manage\/\d+\/smarttools\/?$/.test(path),
  stats: path => /^\/stats/.test(path),
  logout: path => /^\/logout/.test(path),
};

export const getDecagonChatPath = (pathname, search = '') => {
  const searchParams = new URLSearchParams(search);
  searchParams.set('chat_active', true);
  return { pathname, search: searchParams.toString() };
};

export const getTermsUrl = (venuesList, venueId) => {
  const requestedVenue = !!venueId && venuesList.find(v => v.venueId === venueId);
  const activeVenue = requestedVenue || firstOrEmpty(venuesList);

  return LINKS.termsCountryMapping[activeVenue.countryCode] || LINKS.termsCountryMapping.USA;
};

export const getVenueTermsUrl = venue => getTermsUrl([venue, venue.venueId]);

export const getCountryTermsUrl = shortCountryCode => {
  const countryCode = shortCountryCodeToCountryCode[shortCountryCode];
  return LINKS.termsCountryMapping[countryCode] || LINKS.termsCountryMapping.USA;
};

export const getTwoWayCommsLearnMoreLink = (locale = 'en_US') =>
  `https://partnersupport.classpass.com/help/s/article/how-do-i-message-a-user-who-is-booked-in-an-appointment?language=${getLanguage(
    locale
  )}`;

export const getTwoWayCommsUsageGuidelinesLink = () =>
  'https://classpass.com/blog/user-messaging-feature-terms-and-conditions/';

export const getReservationStatusMeaningsLink = (locale = 'en_US') =>
  `https://partnersupport.classpass.com/help/s/article/what-does-each-status-mean-in-my-reservations-report?language=${getLanguage(
    locale
  )}`;

export const addQueryParamsToPath = (currentPath, currentSearchParams, searchParams) => {
  const queryParams = new URLSearchParams(currentSearchParams);
  Object.entries(searchParams).forEach(([key, value]) => {
    queryParams.set(key, value);
  });

  const newPath = `${currentPath}?${queryParams.toString()}`;
  return newPath;
};

export default getTermsUrl;
